import React, { useCallback, memo } from 'react'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'

/**
 * Generic Community empty-state block.
 *
 * Two variants:
 *   - `compact`     — for inside a row body. Divider + copy + inline CTAs.
 *   - `full-height` — for an empty tab. Centered block, ~200px min.
 *
 * CTAs use `Button` with `BUTTON_TYPES.GHOST`. Primary CTA's onClick
 * receives the resolved primary label as its argument, so callers can
 * pass that label through to the AI Guide auto-submit.
 */

export interface CommunityEmptyStateCta {
  label: string
  onClick: (label: string) => void
}

export interface CommunityEmptyStateProps {
  icon?: React.ReactNode
  title?: string
  description?: string
  primaryCta?: CommunityEmptyStateCta
  secondaryCta?: CommunityEmptyStateCta
  variant?: 'compact' | 'full-height'
  className?: string
}

function CommunityEmptyStateBase ({
  icon,
  title,
  description,
  primaryCta,
  secondaryCta,
  variant = 'compact',
  className,
}: CommunityEmptyStateProps) {
  const handlePrimary = useCallback(() => {
    if (primaryCta) primaryCta.onClick(primaryCta.label)
  }, [primaryCta])
  const handleSecondary = useCallback(() => {
    if (secondaryCta) secondaryCta.onClick(secondaryCta.label)
  }, [secondaryCta])

  const rootClassName = [
    'community-empty-state',
    `community-empty-state--${variant}`,
    className,
  ].filter(Boolean).join(' ')

  return (
    <div className={rootClassName}>
      {variant === 'compact' ? (
        <div className="community-empty-state__divider" aria-hidden="true" />
      ) : null}
      <div className="community-empty-state__row">
        {icon ? (
          <span className="community-empty-state__icon" aria-hidden="true">{icon}</span>
        ) : null}
        {title ? (
          <p className="community-empty-state__title">{title}</p>
        ) : null}
        {description ? (
          <p className="community-empty-state__copy">{description}</p>
        ) : null}
        {primaryCta || secondaryCta ? (
          <div className="community-empty-state__ctas">
            {primaryCta ? (
              <Button
                type={BUTTON_TYPES.GHOST}
                className="community-empty-state__cta community-empty-state__cta--primary"
                onClick={handlePrimary}
              >
                {primaryCta.label}
              </Button>
            ) : null}
            {secondaryCta ? (
              <Button
                type={BUTTON_TYPES.GHOST}
                className="community-empty-state__cta community-empty-state__cta--secondary"
                onClick={handleSecondary}
              >
                {secondaryCta.label}
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

const CommunityEmptyState = memo(CommunityEmptyStateBase)
CommunityEmptyState.displayName = 'CommunityEmptyState'
export default CommunityEmptyState
